import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { caForeclosures, CA_COUNTIES_CONFIG } from '../../mocks/caForeclosures';
import { nvForeclosures, NV_COUNTIES_CONFIG } from '../../mocks/nvForeclosures';
import { azForeclosures, AZ_COUNTIES_CONFIG } from '../../mocks/azForeclosures';
import { CombinedProperty } from './types';
import { CombinedLeafletMap } from './components/CombinedLeafletMap';
import { CombinedSidebar } from './components/CombinedSidebar';

type StateFilter = 'All' | 'CA' | 'NV' | 'AZ';

interface Filters {
  minTax: number;
  maxTax: number;
  propertyType: string;
  state: StateFilter;
  status: string;
  counties: Set<string>;
}

const ALL_COUNTY_KEYS = [
  ...Object.keys(CA_COUNTIES_CONFIG).map(c => `CA:${c}`),
  ...Object.keys(NV_COUNTIES_CONFIG).map(c => `NV:${c}`),
  ...Object.keys(AZ_COUNTIES_CONFIG).map(c => `AZ:${c}`),
];

const INITIAL_FILTERS: Filters = {
  minTax: 0,
  maxTax: 50000,
  propertyType: 'All Types',
  state: 'All',
  status: 'All Status',
  counties: new Set(ALL_COUNTY_KEYS),
};

const allProperties: CombinedProperty[] = [
  ...caForeclosures.map(p => ({ ...p, state: 'CA' as const })),
  ...nvForeclosures.map(p => ({ ...p, state: 'NV' as const })),
  ...azForeclosures.map(p => ({ ...p, state: 'AZ' as const })),
];

const STATE_COLORS: Record<string, string> = {
  CA: '#f97316',
  NV: '#f59e0b',
  AZ: '#22c55e',
};

export default function TaxForeclosuresPage() {
  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);
  const [focusState, setFocusState] = useState<string | null>(null);

  const filteredProperties = useMemo(() => {
    return allProperties.filter(p => {
      if (p.taxesOwed < filters.minTax || p.taxesOwed > filters.maxTax) return false;
      if (filters.propertyType !== 'All Types' && p.propertyType !== filters.propertyType) return false;
      if (filters.state !== 'All' && p.state !== filters.state) return false;
      if (filters.status !== 'All Status' && p.status !== filters.status) return false;
      const countyKey = `${p.state}:${p.county}`;
      if (!filters.counties.has(countyKey)) return false;
      return true;
    });
  }, [filters]);

  const handleFocusHandled = useCallback(() => setFocusState(null), []);

  const caCount = filteredProperties.filter(p => p.state === 'CA').length;
  const nvCount = filteredProperties.filter(p => p.state === 'NV').length;
  const azCount = filteredProperties.filter(p => p.state === 'AZ').length;

  return (
    <div className="h-screen flex flex-col bg-[#0f172a] overflow-hidden">
      {/* Top Bar */}
      <div className="bg-[#111827] border-b border-gray-700/50 px-5 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <Link
            to="/"
            className="flex items-center gap-1.5 text-gray-400 hover:text-gray-200 transition-colors text-sm cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line text-sm"></i>
            </div>
            Back
          </Link>
          <div className="h-4 w-px bg-gray-700"></div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-map-2-fill text-amber-400"></i>
            </div>
            <h1 className="text-white font-bold text-base tracking-tight">Multi-State Tax Foreclosures</h1>
            <span className="bg-amber-500/20 text-amber-400 border border-amber-500/30 text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
              BETA
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* State pills */}
          <div className="flex items-center gap-2">
            {(['CA', 'NV', 'AZ'] as const).map(s => (
              <button
                key={s}
                onClick={() => { setFilters(f => ({ ...f, state: f.state === s ? 'All' : s })); setFocusState(s); }}
                className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border transition-all cursor-pointer whitespace-nowrap font-semibold"
                style={{
                  borderColor: `${STATE_COLORS[s]}44`,
                  backgroundColor: filters.state === s ? `${STATE_COLORS[s]}20` : 'transparent',
                  color: filters.state === s ? STATE_COLORS[s] : '#6b7280',
                }}
              >
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: STATE_COLORS[s] }}></span>
                {s}
                <span
                  className="text-xs px-1 py-0.5 rounded"
                  style={{ backgroundColor: `${STATE_COLORS[s]}22`, color: STATE_COLORS[s] }}
                >
                  {s === 'CA' ? caCount : s === 'NV' ? nvCount : azCount}
                </span>
              </button>
            ))}
          </div>

          <div className="h-4 w-px bg-gray-700"></div>

          {/* Status legend */}
          <div className="hidden lg:flex items-center gap-3 text-xs">
            {[
              { label: 'Active', color: '#22c55e' },
              { label: 'Pending', color: '#f59e0b' },
              { label: 'Upcoming', color: '#06b6d4' },
              { label: 'Sold', color: '#9ca3af' },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-1 whitespace-nowrap">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }}></span>
                <span className="text-gray-400">{s.label}</span>
              </div>
            ))}
          </div>

          <div className="h-4 w-px bg-gray-700"></div>

          <div className="flex items-center gap-1.5 bg-[#1f2937] border border-gray-600 rounded-lg px-3 py-1.5">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-map-pin-2-fill text-amber-400 text-xs"></i>
            </div>
            <span className="text-amber-400 font-bold text-sm">{filteredProperties.length}</span>
            <span className="text-gray-400 text-xs">of {allProperties.length}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        <CombinedSidebar
          filters={filters}
          onFiltersChange={setFilters}
          filteredProperties={filteredProperties}
          allProperties={allProperties}
          onFocusState={(s) => setFocusState(s)}
        />

        <div className="flex-1 relative overflow-hidden">
          <CombinedLeafletMap
            properties={filteredProperties}
            focusState={focusState}
            onFocusHandled={handleFocusHandled}
          />

          {filteredProperties.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="bg-[#111827]/90 border border-gray-700 rounded-xl px-8 py-6 text-center">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-map-pin-off-line text-gray-500 text-3xl"></i>
                </div>
                <div className="text-gray-300 font-semibold">No properties match your filters</div>
                <div className="text-gray-500 text-sm mt-1">Try adjusting the filters in the sidebar</div>
              </div>
            </div>
          )}

          {/* Map overlay stats */}
          <div className="absolute top-4 right-4 bg-[#111827]/90 border border-gray-700 rounded-xl px-4 py-3 pointer-events-none">
            <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">Properties Shown</div>
            <div className="text-2xl font-bold text-amber-400 mb-2">{filteredProperties.length}</div>
            <div className="space-y-1">
              {(['CA', 'NV', 'AZ'] as const).map(s => (
                <div key={s} className="flex items-center justify-between gap-4">
                  <span className="text-xs font-semibold" style={{ color: STATE_COLORS[s] }}>{s}</span>
                  <span className="text-xs text-gray-400">
                    {s === 'CA' ? caCount : s === 'NV' ? nvCount : azCount} props
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="absolute bottom-8 left-4 bg-[#111827]/80 border border-gray-700/50 rounded-lg px-3 py-2 pointer-events-none">
            <div className="text-xs text-gray-400">
              <i className="ri-zoom-in-line mr-1"></i>
              Click markers to view property details
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
